import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["link", "day"]
  static values = { week: String, selectedDay: String }

  connect() {
    const url = new URL(window.location.href)
    const day = url.searchParams.get("day")
    // The server can clamp dates (including paid-only weeks for free accounts).
    // Keep the initial history entry consistent with the page actually rendered.
    if (this.weekFor(url.searchParams.get("ref_date")) !== this.weekValue ||
        (day && !this.dayTargets.some(target => target.dataset.date === day))) {
      url.searchParams.set("ref_date", this.weekValue)
      url.searchParams.set("day", this.selectedDayValue)
      history.replaceState(history.state, "", url)
    }
    this.navigationContext = this.contextFor(new URL(window.location.href))
    this.linkContext = this.linkTargets[0] && this.contextFor(new URL(this.linkTargets[0].href))
    this.restore = this.restore.bind(this)
    window.addEventListener("schedule-days:restore", this.restore)
    this.show(new URL(window.location.href))
  }

  disconnect() {
    window.removeEventListener("schedule-days:restore", this.restore)
  }

  select(event) {
    if (!window.matchMedia("(max-width: 1023.98px)").matches ||
        event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const url = new URL(event.currentTarget.href)
    if (!this.localDay(url)) return

    event.preventDefault()
    if (url.href !== window.location.href) history.pushState(history.state, "", url)
    this.show(url)
  }

  restore(event) {
    const url = new URL(window.location.href)
    if (!this.localDay(url)) return

    event.preventDefault()
    this.show(url)
  }

  weekFor(date) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date || "")) return this.weekValue
    const start = new Date(`${date}T00:00:00Z`)
    if (Number.isNaN(start.getTime())) return this.weekValue
    start.setUTCDate(start.getUTCDate() - (start.getUTCDay() + 6) % 7)
    return start.toISOString().slice(0, 10)
  }

  contextFor(url) {
    const context = new URL(url)
    context.searchParams.delete("day")
    context.searchParams.set("ref_date", this.weekFor(context.searchParams.get("ref_date")))
    context.searchParams.sort()
    context.hash = ""
    return context.href
  }

  localDay(url) {
    const date = url.searchParams.get("day") || this.selectedDayValue
    return this.weekFor(url.searchParams.get("ref_date")) === this.weekValue &&
      [this.navigationContext, this.linkContext].includes(this.contextFor(url)) &&
      this.dayTargets.some(day => day.dataset.date === date)
  }

  show(url) {
    if (!this.localDay(url)) return

    const date = url.searchParams.get("day") || this.selectedDayValue
    this.dayTargets.forEach(day => day.classList.toggle("schedule-calendar__day--selected", day.dataset.date === date))
    this.linkTargets.forEach(link => {
      if (new URL(link.href).searchParams.get("day") === date) link.setAttribute("aria-current", "date")
      else link.removeAttribute("aria-current")
    })
    document.querySelectorAll('#filters input[name="day"]').forEach(input => { input.value = date })
    this.updateLinks(date)
  }

  updateLinks(date) {
    const weekday = (new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7
    const links = new Set([...this.element.querySelectorAll("a[href]"),
      ...document.querySelectorAll("#filters a[href], .floating-filter-summary a[href]")])
    links.forEach(link => {
      if (this.linkTargets.includes(link) || link.classList.contains("schedule-calendar__today") ||
          link.getAttribute("href").startsWith("#")) return
      const url = new URL(link.href)
      if (url.origin !== location.origin || url.pathname !== location.pathname || !url.searchParams.has("day")) return
      let selected = date
      if (link.classList.contains("schedule-calendar__arrow")) {
        const start = new Date(`${this.weekFor(url.searchParams.get("ref_date"))}T00:00:00Z`)
        start.setUTCDate(start.getUTCDate() + weekday)
        selected = start.toISOString().slice(0, 10)
      }
      url.searchParams.set("day", selected)
      link.href = url.href
    })
  }
}
